import { useCallback, useEffect, useState } from "react";
import { Download, Palette, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
const FIELDS = [["person_name", "Name on card"], ["person_title", "Title"], ["email", "Email"], ["phone", "Phone"], ["tagline", "Tagline"], ["address", "Address"], ["founder_story", "Founder story (homepage 'Meet the founder' strip)"],
  ["legal_name", "Legal entity name (on bills)"], ["gstin", "GSTIN (printed on bills)"], ["upi_id", "UPI ID (bills + QR)"], ["bank_holder", "Bank account name"], ["bank_name", "Bank name & branch"], ["bank_account", "Account number"], ["bank_ifsc", "IFSC"], ["rental_terms", "Rental agreement terms (one clause per line)"],
  ["promo_text", "Top flash-offer ticker text (moving bar on every page)"], ["promo_link", "Ticker link (e.g. /tours/puri-tour-package-4-days-3-nights)"], ["promo_ends_on", "Offer end date (YYYY-MM-DD) — shows 'ends in N days', bar hides after"], ["promo_enabled", "Ticker on? (yes / no)"]];

function Asset({ a, version }) {
  const [src, setSrc] = useState("");
  const isPdf = a.ext === "pdf";
  useEffect(() => {
    if (isPdf) return undefined;
    let url;
    api.get(`/admin/brand-kit/${a.key}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [a.key, isPdf, version]);
  const download = async () => {
    try {
      const r = await api.get(`/admin/brand-kit/${a.key}`, { responseType: "blob" });
      const link = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: `random-rider-${a.key}.${a.ext}` });
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="bg-white border border-ink/10 rounded-2xl p-4 flex flex-col" data-testid={`brand-asset-${a.key}`}>
      <div className={`rounded-xl overflow-hidden border border-ink/10 grid place-items-center bg-[repeating-conic-gradient(#f3ede3_0_25%,#fff_0_50%)] bg-[length:20px_20px] ${/^(letterhead|brochure|rate-card|standee)/.test(a.key) ? "aspect-[1/1.3]" : "aspect-[2/1]"}`}>
        {isPdf ? <span className="font-mono text-xs uppercase tracking-widest text-ink/50">PDF · {a.size}</span>
          : src ? <img src={src} alt={a.label} className="w-full h-full object-contain" data-testid={`brand-preview-${a.key}`} /> : <span className="text-xs text-ink/40">Rendering…</span>}
      </div>
      <p className="text-sm font-semibold text-ink mt-3 leading-snug">{a.label}</p>
      <p className="text-[11px] text-ink/50 font-mono">{a.size}</p>
      <button onClick={download} data-testid={`brand-download-${a.key}`} className="mt-3 inline-flex items-center justify-center gap-1.5 bg-ink text-sand text-xs font-semibold py-2.5 rounded-full hover:bg-terra transition-colors"><Download size={13} /> Download .{a.ext}</button>
    </div>
  );
}

export default function BrandKit() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [version, setVersion] = useState(0);
  const load = useCallback(() => api.get("/admin/brand-kit").then((r) => { setData(r.data); setForm(r.data.brand); }).catch(() => toast.error("Could not load brand kit")), []);
  useEffect(() => { load(); }, [load]);
  if (!data || !form) return <p className="text-sm text-ink/50" data-testid="brand-kit-loading">Loading brand kit…</p>;

  const save = async (e) => {
    e.preventDefault();
    await api.put("/admin/brand-kit", form);
    toast.success("Brand details saved — re-rendering assets");
    setVersion((v) => v + 1);
  };

  const groups = data.assets.reduce((acc, a) => { (acc[a.group] ||= []).push(a); return acc; }, {});
  return (
    <div className="space-y-8" data-testid="brand-kit">
      <form onSubmit={save} className="bg-white border border-ink/10 rounded-3xl p-6 grid sm:grid-cols-2 gap-3" data-testid="brand-settings-form">
        <h2 className="sm:col-span-2 font-serif text-2xl font-bold text-ink flex items-center gap-2"><Palette size={20} className="text-terra" /> Brand kit</h2>
        <p className="sm:col-span-2 text-xs text-ink/60 -mt-2">Visiting card, letterhead, brochures, rate cards and QR standees are generated from these details and your logo. Print files are 300 dpi. Standee QR opens the Rentals booking section on randomrider.com.</p>
        {FIELDS.map(([k, label]) => (
          <div key={k} className={["address", "tagline", "founder_story", "rental_terms", "promo_text"].includes(k) ? "sm:col-span-2" : ""}>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">{label}</label>
            {k === "founder_story" || k === "rental_terms" || k === "promo_text"
              ? <textarea rows={k === "rental_terms" ? 10 : k === "promo_text" ? 3 : 4} maxLength={k === "rental_terms" ? 4000 : 1200} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`brand-field-${k}`} />
              : <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`brand-field-${k}`} />}
          </div>
        ))}
        <div className="sm:col-span-2"><button type="submit" data-testid="brand-save-button" className="inline-flex items-center gap-2 bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors"><Save size={14} /> Save & re-render</button></div>
      </form>
      {Object.entries(groups).map(([g, items]) => (
        <section key={g} data-testid={`brand-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}>
          <h3 className="font-mono text-xs uppercase tracking-widest text-ink/60 mb-3">{g} <span className="text-ink/40">· {items.length}</span></h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brand-assets">
            {items.map((a) => <Asset key={a.key} a={a} version={version} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
